import React, { useContext } from 'react';

import UIContext from 'components/Utils/UIContext';
import { faClone, faClose, faEdit, faEyeSlash, faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionButton from "components/action/ActionButton";

import { useTranslation } from "react-i18next";

import "./RiskGeneralTab.sass";
import { Button, Tag, Tooltip, Flex } from 'antd';

import { find, keyBy, sortBy } from 'lodash';
import { getScoreColor } from 'utils/ranking';
import Weight from 'components/controls/Weight';
import { isRiskOrOpportunityImpactBetter, isRiskOrOpportunityLikelihoodBetter, isRiskOrOpportunityLikelihoodWorse, isRiskOrOpportunityWorse } from 'utils/rules';
import { ImpactRelationSection } from 'components/tabs/ImpactRelationSection';
import { FishboneLinksReadonly } from 'components/tabs/FishboneLinksReadonly';
import { useIndicatorImpactController } from 'hooks/useIndicatorImpactController';

interface RiskGeneralTabProps {
    selection: any;
    onEdit: Function;
    onClone: Function;
    onClose: Function;
    onDisable: Function;
    onDelete: Function;
    scenario: any;

    categories: any[];
    likelihoods: object;
    impacts: object;

    stakes: any[];
    opportunities: any[];
    risks?: any[];
    indicators?: any[];
    fishboneBranchs?: any[];
    createStakeImpact: Function;
    deleteStakeImpact: Function;
    updateImpactStrength: Function;

    onSetOpeningIds: Function;
    onCycleOpeningLink: Function;
}

// n-n : openings liées à un risque — la table de liens est la seule source
// de vérité (cut-over E1, matchingOpportunityId @deprecated).
// Le lien (avec son strength) entre `risk` et l'opening `openingId`, ou null.
const findOpeningLink = (risk: any, openingId: string): any =>
    (risk?.openingLinks || []).find((l: any) => l.openingId === openingId) || null;

const getImpactingStakeDetails = (impactedStakes, stakeId: string) => {
    return find(
        impactedStakes || [],
        (ii: any) => ii.stakeId == stakeId
    );
};

const isImpactingStake = (indexedStakes, impactedStakes, stakeId: string) => {
    if (
        indexedStakes[stakeId] &&
        impactedStakes
            ?.map((ii: any) => ii.stakeId)
            .includes(stakeId)
    )
        return true;
    return false;
};

export const RiskGeneralTab = (props: RiskGeneralTabProps) => {

    const { categories = [], stakes = [], opportunities = [] } = props;
    const { t } = useTranslation();
    const uiContext = useContext(UIContext);

    const indexedStakes = keyBy(stakes, 'id');

    const { createRiskIndicatorImpact, updateRiskIndicatorImpactStrength, deleteRiskIndicatorImpact } = useIndicatorImpactController();

    const isImpactBetter = isRiskOrOpportunityImpactBetter(props.selection);
    const isImpactWorse = isRiskOrOpportunityWorse(props.selection);
    const isLikehoodBetter = isRiskOrOpportunityLikelihoodBetter(props.selection);
    const isLikehoodWorse = isRiskOrOpportunityLikelihoodWorse(props.selection);


    const category = categories.find(
        (c: any) => c.id === props.selection?.riskCategoryId
    );

    return (
        <div className="RiskGeneralTab">

            <Flex vertical>

                <Flex className="mb-1">
                    <div className="w-100 text-left">
                        <div className="title">{props.selection?.name}</div>
                        <small>{t(props.selection?.isOpportunity ? "models.risk.name_positive" : "models.risk.name")}</small>
                    </div>

                    <Tooltip title={t('actions.close')} placement='left'>
                        <Button
                            shape="circle"
                            variant="solid"
                            color="primary"
                            className="mini ml-auto"
                            tooltip={t('models.item.actions.edit')}
                            style={{ marginRight: '-0.9rem' }}
                            onClick={props.onClose}
                        >
                            <FontAwesomeIcon icon={faClose} />
                        </Button>
                    </Tooltip>
                </Flex>

                {props.selection?.description && (
                    <div className="risk-description">{props.selection?.description}</div>
                )}

                <ul style={{ fontSize: "0.8rem" }} className="mb-1">
                    <li className="EditableListItem">
                        <div style={{ width: '40%', textAlign: 'left' }}>
                            {t('models.risk.fields.risk_category')}
                        </div>
                        <div>
                            {category ? category.name : t("models.risk.messages.no_category")}
                        </div>
                    </li>
                    <li className="EditableListItem">
                        <div style={{ width: '40%', textAlign: 'left' }}>
                            {t('models.risk.fields.impact')}
                        </div>
                        <div>
                            <div className={[isImpactBetter ? "positive" : "", isImpactWorse ? "negative" : ""].join(" ")}>
                                {props.impacts[parseInt(props.selection?.impact)] ? props.selection?.impact + " - " + props.impacts[parseInt(props.selection?.impact)].name : props.selection?.impact}
                            </div>
                        </div>
                    </li>
                    <li className="EditableListItem">
                        <div style={{ width: '40%', textAlign: 'left' }}>
                            {t('models.risk.fields.likelihood')}
                        </div>
                        <div>
                            <div className={[isLikehoodBetter ? "positive" : "", isLikehoodWorse ? "negative" : ""].join(" ")}>
                                {props.likelihoods[parseInt(props.selection?.likelihood)] ? props.selection?.likelihood + " - " + props.likelihoods[parseInt(props.selection?.likelihood)].name : props.selection?.likelihood}
                            </div>
                        </div>
                    </li>
                </ul>

                {uiContext.canEdit && <div className="Actions mt-1 mb-1 pl-1 text-center ml-auto mr-auto" data-testid="risk-actions">
                    <ActionButton
                        className="small ml-05"
                        tooltip={t(props.selection?.isOpportunity ? 'models.risk.actions.edit_opportunity' : 'models.risk.actions.edit')}
                        data-testid="action-edit"
                        onClick={() => props.onEdit(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </ActionButton>

                    {false && <ActionButton
                        className="small"
                        tooltip={t('models.risk.actions.clone')}
                        data-testid="action-clone"
                        onClick={() => props.onClone(props.selection)}
                    >
                        <FontAwesomeIcon icon={faClone} />
                    </ActionButton>}

                    {props.onDisable && <ActionButton
                        className="small"
                        tooltip={t('actions.disable')}
                        data-testid="action-disable"
                        onClick={() => props.onDisable(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEyeSlash} />
                    </ActionButton>}

                    {!props.scenario && <ActionButton
                        className="small"
                        tooltip={t(props.selection?.isOpportunity ? 'models.risk.actions.delete_opportunity' : 'models.risk.actions.delete')}
                        data-testid="action-delete"
                        onClick={() => props.onDelete(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </ActionButton>}
                </div>}

                <FishboneLinksReadonly
                    title={t('pages.fishbone.title')}
                    links={props.selection?.fishboneLinks}
                    fishboneBranchs={props.fishboneBranchs}
                />

                {(
                    <Flex vertical className="mt-2">
                        <div className="text-left mb-05">
                            <strong>{t('models.item.fields.stakes.name')}</strong>
                        </div>
                        <div className="text-left">
                            {stakes && stakes.length ? (
                                sortBy(stakes, 'name').map(
                                    (i: any, index) => {
                                        const impactedStake =
                                            getImpactingStakeDetails(props.selection?.impactedStakes || [], i.id);

                                        return (
                                            <Tag.CheckableTag
                                                key={index}
                                                checked={isImpactingStake(indexedStakes, props.selection?.impactedStakes || [], i.id)}
                                                onClick={async () => {
                                                    if (!uiContext.canEdit) return;

                                                    if (
                                                        !impactedStake ||
                                                        !impactedStake.id
                                                    ) {
                                                        props.createStakeImpact(
                                                            props.selection?.id,
                                                            i.id
                                                        );
                                                    } else if (
                                                        impactedStake.strength <
                                                        3
                                                    ) {
                                                        props.updateImpactStrength(
                                                            impactedStake,
                                                            impactedStake.strength +
                                                            1
                                                        );
                                                    } else {
                                                        props.deleteStakeImpact(
                                                            impactedStake.id,
                                                            props.selection?.id
                                                        );
                                                    }
                                                }}
                                                className={[
                                                    'ImpactedStake tag'
                                                ].join(' ')}
                                            >
                                                <div
                                                    className={
                                                        'Trend v' + i.trend
                                                    }
                                                    style={
                                                        i.trend == '50'
                                                            ? {}
                                                            : {
                                                                background:
                                                                    getScoreColor(
                                                                        i.trend
                                                                    )
                                                            }
                                                    }
                                                ></div>
                                                <span className="tag__label" title={i.name}>{i.name}</span>

                                                {impactedStake ? (
                                                    <Weight onClick={() => { }}>
                                                        {
                                                            impactedStake.strength
                                                        }
                                                    </Weight>
                                                ) : null}
                                            </Tag.CheckableTag>
                                        );
                                    }
                                )
                            ) : (
                                <div
                                    className="text-center"
                                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                                >
                                    {t('models.stake.no_x')}
                                </div>
                            )}
                        </div>
                    </Flex>
                )}

                {!props.selection?.isOpportunity && (
                    <Flex vertical className="mt-2">
                        <div className="text-left mb-05">
                            <strong>{t('models.item.fields.opportunities.name')}</strong>
                        </div>
                        <div className="text-left">
                            {opportunities && opportunities.length ? (
                                sortBy(opportunities, 'name').map(
                                    (o: any, index) => {
                                        const link = findOpeningLink(props.selection, o.id);
                                        const isLinked = !!link;

                                        return (
                                            <Tag.CheckableTag
                                                key={index}
                                                checked={isLinked}
                                                onClick={async () => {
                                                    if (!uiContext.canEdit) return;
                                                    // Cycle 1->2->3->suppression (le lien est porte par ce risque).
                                                    props.onCycleOpeningLink(props.selection, o.id);
                                                }}
                                                className={[
                                                    'ImpactedStake tag'
                                                ].join(' ')}
                                            >
                                                <span className="tag__label" title={o.name}>{o.name}</span>

                                                {link ? (
                                                    <Weight onClick={() => { }}>
                                                        {link.strength || 1}
                                                    </Weight>
                                                ) : null}
                                            </Tag.CheckableTag>
                                        );
                                    }
                                )
                            ) : (
                                <div
                                    className="text-center"
                                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                                >
                                    {t('models.stake.no_x')}
                                </div>
                            )}
                        </div>
                    </Flex>
                )}

                {props.selection?.isOpportunity && (
                    <Flex vertical className="mt-2">
                        <div className="text-left mb-05">
                            <strong>{t('models.risk.fields.matching_risks')}</strong>
                        </div>
                        <div className="text-left">
                            {(props.risks || []).length ? (
                                sortBy(props.risks, 'name').map(
                                    (r: any, index) => {
                                        // Réciproque : ce tag cycle le lien
                                        // SUR LE RISQUE r (vers cette opening),
                                        // sans toucher ses autres liens.
                                        const link = findOpeningLink(r, props.selection?.id);
                                        const isLinked = !!link;

                                        return (
                                            <Tag.CheckableTag
                                                key={index}
                                                checked={isLinked}
                                                onClick={async () => {
                                                    if (!uiContext.canEdit) return;
                                                    // Cycle 1->2->3->suppression (le lien est porte par le risque r).
                                                    props.onCycleOpeningLink(r, props.selection?.id);
                                                }}
                                                className={[
                                                    'ImpactedStake tag'
                                                ].join(' ')}
                                            >
                                                <span className="tag__label" title={r.name}>{r.name}</span>

                                                {link ? (
                                                    <Weight onClick={() => { }}>
                                                        {link.strength || 1}
                                                    </Weight>
                                                ) : null}
                                            </Tag.CheckableTag>
                                        );
                                    }
                                )
                            ) : (
                                <div
                                    className="text-center"
                                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                                >
                                    {t('models.risk.no_x', { defaultValue: t('models.stake.no_x') })}
                                </div>
                            )}
                        </div>
                    </Flex>
                )}

                <ImpactRelationSection
                    title={t('models.item.fields.indicators.name')}
                    items={props.indicators || []}
                    scenario={props.scenario}
                    emptyMessage={t('models.indicator.no_x')}
                    getLink={(ind) =>
                        (props.selection?.impactedIndicators || []).find(
                            (x: any) => x.indicatorId === ind.id
                        )
                    }
                    onToggle={(ind, link) => {
                        if (!link || !link.id) {
                            createRiskIndicatorImpact(props.selection?.id, ind.id);
                        } else if (link.strength < 3) {
                            updateRiskIndicatorImpactStrength(link, link.strength + 1);
                        } else {
                            deleteRiskIndicatorImpact(link.id, props.selection?.id);
                        }
                    }}
                />

            </Flex>
        </div>
    );
}